/**
 * 资产报表打印配置（7 个报表的 buildPrintData）
 * 遵循 print-template 报表打印调用文档：buildData(dataSource, queryParam, userInfo, companyInfo) => Object
 * dataSource 为列表页当前已加载数据（由调用方传入对应 tab 的 rows + summary 合并对象）
 * 打印模板走 jsh_print_template 表两级机制（租户模板优先，无则用系统默认模板 tenant_id=0）
 */
const METHOD_MAP = { 1: '年限平均法', 2: '工作量法', 3: '双倍余额递减', 4: '年数总和法' }

function methodText(m) {
  return METHOD_MAP[m] || m || ''
}

function baseResult(title, companyInfo) {
  return {
    reportTitle: title,
    reportDate: new Date().toLocaleDateString(),
    companyName: (companyInfo && companyInfo.companyName) || ''
  }
}

/** 1. 折旧明细表（ASSET_DEP_DETAIL）—— dataSource: { rows: depRows, summary: depSummary, query: depQuery } */
export function buildDepPrintData(dataSource, queryParam, userInfo, companyInfo) {
  const src = dataSource || {}
  const rows = (src.rows || []).map((r, idx) => ({
    rowIndex: idx + 1,
    period: r.period || '',
    assetCode: r.assetCode || '',
    assetName: r.assetName || '',
    categoryName: r.categoryName || '',
    departmentName: r.departmentName || '',
    method: methodText(r.method),
    depreciationAmount: r.depreciationAmount == null ? '' : r.depreciationAmount,
    accumulatedDepreciation: r.accumulatedDepreciation == null ? '' : r.accumulatedDepreciation,
    netValue: r.netValue == null ? '' : r.netValue
  }))
  return {
    ...baseResult('折旧明细表', companyInfo),
    totalDepreciation: (src.summary && src.summary.depreciationAmount != null) ? src.summary.depreciationAmount : '',
    table: rows
  }
}

/** 2. 资产台账（ASSET_LEDGER）—— dataSource: { rows: ledgerRows, summary: ledgerSummary } */
export function buildLedgerPrintData(dataSource, queryParam, userInfo, companyInfo) {
  const src = dataSource || {}
  const rows = (src.rows || []).map((r, idx) => ({
    rowIndex: idx + 1,
    assetCode: r.assetCode || '',
    assetName: r.assetName || '',
    assetModel: r.assetModel || '',
    categoryName: r.categoryName || '',
    departmentName: r.departmentName || '',
    originalValue: r.originalValue == null ? '' : r.originalValue,
    initialAccumDep: r.initialAccumDep == null ? '' : r.initialAccumDep,
    periodDepreciation: r.periodDepreciation == null ? '' : r.periodDepreciation,
    accumulatedDepreciation: r.accumulatedDepreciation == null ? '' : r.accumulatedDepreciation,
    impairmentAmount: r.impairmentAmount == null ? '' : r.impairmentAmount,
    netValue: r.netValue == null ? '' : r.netValue,
    status: r.status || ''
  }))
  return {
    ...baseResult('资产台账', companyInfo),
    totalOriginalValue: (src.summary && src.summary.originalValue != null) ? src.summary.originalValue : '',
    table: rows
  }
}

/** 3. 变动汇总表（ASSET_CHANGE_SUMMARY）—— dataSource: { rows: changeRows } */
export function buildChangePrintData(dataSource, queryParam, userInfo, companyInfo) {
  const src = dataSource || {}
  let total = 0
  const rows = (src.rows || []).map((r, idx) => {
    total += Number(r.changeAmount) || 0
    return {
      rowIndex: idx + 1,
      billNo: r.billNo || '',
      billTimeStr: (r.billTime || '').substring(0, 10),
      assetCode: r.assetCode || '',
      assetName: r.assetName || '',
      changeType: r.changeType || '',
      beforeValue: r.beforeValue == null ? '' : r.beforeValue,
      afterValue: r.afterValue == null ? '' : r.afterValue,
      changeAmount: r.changeAmount == null ? '' : r.changeAmount,
      operator: r.operator || ''
    }
  })
  return {
    ...baseResult('变动汇总表', companyInfo),
    totalChangeAmount: total,
    table: rows
  }
}

/** 4. 闲置资产清单（ASSET_IDLE_LIST）—— dataSource: { rows: idleRows } */
export function buildIdlePrintData(dataSource, queryParam, userInfo, companyInfo) {
  const src = dataSource || {}
  const rows = (src.rows || []).map((r, idx) => ({
    rowIndex: idx + 1,
    assetCode: r.assetCode || '',
    assetName: r.assetName || '',
    categoryName: r.categoryName || '',
    departmentName: r.departmentName || '',
    originalValue: r.originalValue == null ? '' : r.originalValue,
    accumulatedDepreciation: r.accumulatedDepreciation == null ? '' : r.accumulatedDepreciation,
    netValue: r.netValue == null ? '' : r.netValue,
    idleMonths: r.idleMonths == null ? '' : r.idleMonths,
    lastStatusChangeDate: (r.lastStatusChangeDate || '').substring(0, 10)
  }))
  return {
    ...baseResult('闲置资产清单', companyInfo),
    table: rows
  }
}

/** 5. 业财对账表（ASSET_BIZFIN_CHECK）—— dataSource: { rows: bizfinRows, period: 'YYYY-MM' } */
export function buildBizfinPrintData(dataSource, queryParam, userInfo, companyInfo) {
  const src = dataSource || {}
  const rows = (src.rows || []).map((r, idx) => ({
    rowIndex: idx + 1,
    subjectCode: r.subjectCode || '',
    subjectName: r.subjectName || '',
    assetValue: r.assetValue == null ? '' : r.assetValue,
    glValue: r.glValue == null ? '' : r.glValue,
    diff: r.diff == null ? '' : r.diff
  }))
  return {
    ...baseResult('业财对账表', companyInfo),
    period: src.period || '',
    table: rows
  }
}

/** 6. 税会差异调节表（ASSET_TAX_ADJUST）—— dataSource: { rows: taxRows, summary: taxSummary, period } */
export function buildTaxPrintData(dataSource, queryParam, userInfo, companyInfo) {
  const src = dataSource || {}
  const s = src.summary || {}
  const rows = (src.rows || []).map((r, idx) => ({
    rowIndex: idx + 1,
    assetCode: r.assetCode || '',
    assetName: r.assetName || '',
    originalValue: r.originalValue == null ? '' : r.originalValue,
    acctCurDep: r.acctCurDep == null ? '' : r.acctCurDep,
    taxCurDep: r.taxCurDep == null ? '' : r.taxCurDep,
    curDiff: r.curDiff == null ? '' : r.curDiff,
    acctCumDep: r.acctCumDep == null ? '' : r.acctCumDep,
    taxCumDep: r.taxCumDep == null ? '' : r.taxCumDep,
    cumDiff: r.cumDiff == null ? '' : r.cumDiff
  }))
  return {
    ...baseResult('税会差异调节表', companyInfo),
    period: src.period || '',
    totalAcctCurDep: s.acctCurDep == null ? '' : s.acctCurDep,
    totalTaxCurDep: s.taxCurDep == null ? '' : s.taxCurDep,
    table: rows
  }
}

/** 7. 供应商往来对账（ASSET_SUPPLIER_STATEMENT）—— dataSource: { rows: supplierRows, summary: supplierSummary } */
export function buildSupplierPrintData(dataSource, queryParam, userInfo, companyInfo) {
  const src = dataSource || {}
  const rows = (src.rows || []).map((r, idx) => ({
    rowIndex: idx + 1,
    supplier: r.supplier || '',
    contacts: r.contacts || '',
    telephone: r.telephone || '',
    beginNeed: r.beginNeed == null ? '' : r.beginNeed,
    goodsDebt: r.goodsDebt == null ? '' : r.goodsDebt,
    assetDebt: r.assetDebt == null ? '' : r.assetDebt,
    assetPay: r.assetPay == null ? '' : r.assetPay,
    pay: r.pay == null ? '' : r.pay,
    endNeed: r.endNeed == null ? '' : r.endNeed
  }))
  return {
    ...baseResult('供应商往来对账', companyInfo),
    totalEndNeed: (src.summary && src.summary.endNeed != null) ? src.summary.endNeed : '',
    table: rows
  }
}
